import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Image, TouchableOpacity} from 'react-native';
import {
  Text, Button, Chip, ActivityIndicator,
  Dialog, Portal,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import {paymentApi} from '../../services/api/paymentApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import WorkAssignmentStatusDialog from '../order/WorkAssignmentStatusDialog';

const MODE_LABELS = {CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque'};

const EMPLOYMENT_LABELS = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  FREELANCER: 'Freelancer',
  DAILY_WAGE: 'Daily Wage',
  PIECE_RATE: 'Piece Rate',
  COMMISSION_BASED: 'Commission Based',
  OUTSOURCED: 'Outsourced',
  TEMPORARY: 'Temporary',
  INTERN: 'Intern',
};

const getMonthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DetailRow = ({label, value, icon}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <View style={styles.detailLabelContainer}>
        {icon && <Icon name={icon} size={16} color={colors.textSecondary} style={{marginRight: 4}} />}
        <Text style={styles.detailLabel}>{label}</Text>
      </View>
      <Text style={styles.detailValue} numberOfLines={2}>{value}</Text>
    </View>
  );
};

const WorkerDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {workerId} = route.params;

  const [worker, setWorker] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [dressAssignments, setDressAssignments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [workerRes, assignRes, dressRes, payoutsRes] = await Promise.all([
        workerApi.getById(workerId),
        workerApi.getTaskAssignments(),
        workAssignmentApi.getByWorker(workerId),
        paymentApi.getHistory({workerId, from: getMonthStartISO(), to: getTodayISO()}),
      ]);
      setWorker(workerRes.data.data);
      const allSummary = assignRes.data.data || [];
      setAssignments(allSummary.filter(a => a.workerId === workerId));
      const allDress = dressRes.data?.data || [];
      setDressAssignments(allDress.filter(a => a.status !== 'COMPLETED' && a.status !== 'PAID'));
      setPayouts(payoutsRes.data?.data || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, t]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleDeactivate = useCallback(async () => {
    setDeactivateVisible(false);
    try {
      await workerApi.delete(workerId);
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, navigation, t]);

  const handleReactivate = useCallback(async () => {
    try {
      await workerApi.reactivate(workerId);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, fetchData, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={worker?.fullName || ''}
        onBack={() => navigation.goBack()}>
        <TouchableOpacity onPress={() => navigation.navigate('WorkerForm', {workerId})} style={{padding: 8}}>
          <Icon name="pencil" size={22} color={colors.primary} />
        </TouchableOpacity>
      </GradientHeader>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {worker && (
          <>
            {/* Avatar & Name */}
            <View style={styles.avatarSection}>
              {worker.avatarUrl ? (
                <Image source={{uri: worker.avatarUrl}} style={styles.avatar} />
              ) : (
                <View style={styles.avatarFallback}>
                  <Text style={styles.avatarText}>{(worker.fullName || '?').charAt(0).toUpperCase()}</Text>
                </View>
              )}
              <Text style={styles.name}>{worker.fullName}</Text>
              {worker.workerCode ? (
                <Text style={styles.codeText}>{worker.workerCode}</Text>
              ) : null}
              <View style={styles.badgeRow}>
                <Chip
                  style={[styles.statusChip, {backgroundColor: worker.isActive ? colors.success + '15' : colors.error + '15'}]}
                  compact>
                  <Text style={{fontSize: 12, fontWeight: '600', color: worker.isActive ? colors.success : colors.error, marginTop: 2}}>
                    {worker.isActive ? t('worker.active') : t('worker.inactive')}
                  </Text>
                </Chip>
                {worker.workerTypeName && (
                  <Chip style={styles.typeChip} compact>
                    <Text style={{fontSize: 12, fontWeight: '600', color: colors.primary, marginTop: 2}}>
                      {worker.workerTypeName}
                    </Text>
                  </Chip>
                )}
              </View>
            </View>

            {/* Details */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('worker.details')}</Text>
              <DetailRow icon="phone-outline" label={t('worker.phone')} value={worker.phone} />
              <DetailRow icon="email-outline" label={t('worker.email')} value={worker.email} />
              <DetailRow icon="briefcase-outline" label="Employment" value={worker.employmentType ? EMPLOYMENT_LABELS[worker.employmentType] : null} />
              <DetailRow icon="store" label={t('worker.primaryOrgUnit')} value={worker.primaryOrgUnitName} />
              <DetailRow icon="account-outline" label={t('worker.linkedUser')} value={worker.linkedUsername || (worker.userId ? `User #${worker.userId}` : null)} />
            </View>

            {/* Assigned Stores */}
            {worker.assignedOrgUnits && worker.assignedOrgUnits.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('worker.assignedOrgUnits')}</Text>
                {worker.assignedOrgUnits.map(ou => (
                  <View key={ou.id} style={styles.storeRow}>
                    <Icon name="store" size={16} color={colors.primary} />
                    <Text style={styles.storeName}>{ou.name}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Notes */}
            {worker.notes ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('worker.notes')}</Text>
                <Text style={styles.notesText}>{worker.notes}</Text>
              </View>
            ) : null}

            {/* Current Task Assignments */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Current Task Assignments</Text>
              {assignments.length === 0 ? (
                <View style={styles.sectionEmpty}>
                  <Icon name="clipboard-check-outline" size={24} color={colors.textLight} />
                  <Text style={styles.emptyText}>{t('worker.noAssignments')}</Text>
                </View>
              ) : (
                assignments.map((a, idx) => (
                  <TouchableOpacity
                    key={`${a.taskTypeId}-${idx}`}
                    activeOpacity={0.7}
                    onPress={() => navigation.navigate('TaskAssignmentDetail', {
                      workerId: a.workerId,
                      workerName: worker?.fullName,
                      taskTypeId: a.taskTypeId,
                      taskTypeName: a.taskTypeName,
                    })}>
                    <View style={styles.assignmentRow}>
                      <View style={styles.assignmentLeft}>
                        <View style={[styles.assignmentIcon, {backgroundColor: colors.info + '15'}]}>
                          <Icon name="clipboard-check-outline" size={18} color={colors.info} />
                        </View>
                        <Text style={styles.assignmentName}>{a.taskTypeName}</Text>
                      </View>
                      <View style={styles.jobCountBadge}>
                        <Text style={styles.jobCountText}>{a.jobCount} {a.jobCount === 1 ? 'job' : 'jobs'}</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </View>

            {/* Current Dress Assignments */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Current Dress Assignments</Text>
              {dressAssignments.length === 0 ? (
                <View style={styles.sectionEmpty}>
                  <Icon name="hanger" size={24} color={colors.textLight} />
                  <Text style={styles.emptyText}>No active dress assignments</Text>
                </View>
              ) : (
                dressAssignments.map((da, idx) => (
                  <TouchableOpacity
                    key={da.id || idx}
                    activeOpacity={0.7}
                    onPress={() => {
                      setStatusAssignment(da);
                      setStatusDialogVisible(true);
                    }}>
                    <View style={styles.assignmentRow}>
                      <View style={styles.assignmentLeft}>
                        <View style={[styles.assignmentIcon, {backgroundColor: colors.secondary + '15'}]}>
                          <Icon name="tshirt-crew-outline" size={18} color={colors.secondary} />
                        </View>
                        <View style={{flex: 1}}>
                          <Text style={styles.assignmentName}>{da.catalogItemName || '-'}</Text>
                          <Text style={styles.assignmentMeta}>
                            {da.instanceCode}{da.pieceRate != null ? `  \u00B7  \u20B9${Number(da.pieceRate)}` : ''}
                            {da.assignedAt ? `  \u00B7  ${new Date(da.assignedAt).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'})}` : ''}
                          </Text>
                        </View>
                      </View>
                      <Chip
                        style={{backgroundColor: (da.status === 'STARTED' ? colors.primary : colors.textSecondary) + '15', height: 26}}
                        compact>
                        <Text style={{fontSize: 11, fontWeight: '600', color: da.status === 'STARTED' ? colors.primary : colors.textSecondary, marginTop: 2}}>
                          {da.status?.replace('_', ' ')}
                        </Text>
                      </Chip>
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </View>

            {/* Worker Payouts — Current Month */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Payouts This Month</Text>
              {payouts.length === 0 ? (
                <View style={styles.sectionEmpty}>
                  <Icon name="cash" size={24} color={colors.textLight} />
                  <Text style={styles.emptyText}>No payouts this month</Text>
                </View>
              ) : (
                payouts.map((p, idx) => (
                  <View key={p.id || idx} style={styles.assignmentRow}>
                    <View style={styles.assignmentLeft}>
                      <View style={[styles.assignmentIcon, {backgroundColor: colors.success + '15'}]}>
                        <Icon name="cash" size={18} color={colors.success} />
                      </View>
                      <View style={{flex: 1}}>
                        <Text style={styles.assignmentName}>{p.transactionCode}</Text>
                        <Text style={styles.assignmentMeta}>
                          {MODE_LABELS[p.paymentMode] || p.paymentMode}
                          {p.sourceType === 'BULK_PAYMENT' ? '  \u00B7  Bulk' : ''}
                          {'  \u00B7  '}{p.itemCount} item{p.itemCount !== 1 ? 's' : ''}
                          {p.paymentDate ? `  \u00B7  ${new Date(p.paymentDate).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'})}` : ''}
                        </Text>
                      </View>
                    </View>
                    <Text style={styles.payoutAmount}>{'\u20B9'}{Number(p.totalAmount).toLocaleString()}</Text>
                  </View>
                ))
              )}
            </View>

            {/* Action Buttons */}
            <View style={styles.actionSection}>
              {worker.isActive ? (
                <Button
                  mode="outlined"
                  icon="account-off"
                  textColor={colors.error}
                  style={styles.deactivateButton}
                  onPress={() => setDeactivateVisible(true)}>
                  {t('worker.deactivate')}
                </Button>
              ) : (
                <Button
                  mode="outlined"
                  icon="account-check"
                  textColor={colors.success}
                  style={styles.reactivateButton}
                  onPress={handleReactivate}>
                  {t('worker.reactivate')}
                </Button>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('worker.deactivateMessage', {name: worker?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('worker.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('worker.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchData}
        />
      )}

      {snackbar ? (
        <View style={styles.snackbarContainer}>
          <Text style={styles.snackbarText}>{snackbar}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingBottom: spacing.md},

  // Avatar section
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
  },
  avatar: {width: 72, height: 72, borderRadius: 36, marginBottom: spacing.sm},
  avatarFallback: {
    width: 72, height: 72, borderRadius: 36, backgroundColor: colors.secondary,
    justifyContent: 'center', alignItems: 'center', marginBottom: spacing.sm,
  },
  avatarText: {color: colors.white, fontSize: 32, fontWeight: '700'},
  name: {fontSize: 20, fontWeight: '700', color: colors.text},
  codeText: {
    fontSize: 12, color: colors.textSecondary, backgroundColor: colors.background,
    paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, marginTop: 4,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  statusChip: {height: 26},
  typeChip: {backgroundColor: colors.primary + '15', height: 26},

  // Sections
  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Detail rows
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  detailLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
    marginLeft: spacing.md,
  },

  // Store rows
  storeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  storeName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
  },

  // Notes
  notesText: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 20,
  },
  sectionEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  emptyText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },

  // Assignment rows (tasks, dress, payouts)
  assignmentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  assignmentLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  assignmentIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    justifyContent: 'center',
    alignItems: 'center',
  },
  assignmentName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  assignmentMeta: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 1,
  },
  jobCountBadge: {
    backgroundColor: colors.primary + '12',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
  },
  jobCountText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },

  // Payout amount
  payoutAmount: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.success,
  },

  // Actions
  actionSection: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.md,
  },
  deactivateButton: {borderColor: colors.error, borderRadius: 8},
  reactivateButton: {borderColor: colors.success, borderRadius: 8},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
  snackbarContainer: {
    position: 'absolute', bottom: spacing.md, left: spacing.md, right: spacing.md,
    backgroundColor: '#333', borderRadius: 8, padding: spacing.md,
  },
  snackbarText: {color: '#fff', fontSize: 14},
});

export default WorkerDetailScreen;
